import { useEffect, useRef } from "react";

import type { BlsState } from "../../domain/blsChannel";
import { dotPosition } from "../../domain/blsMotion";
import { BLS_DOT_HEX, BLS_DOT_SCALE, BLS_FIELD_COLOR, BLS_MIN_DOT_PX } from "./blsAppearance";

type Props = {
    state: BlsState;
    cyclesNow: (nowEpochMs?: number) => number;
    className?: string;
};

/**
 * The moving dot.
 *
 * Moved with a CSS transform from `requestAnimationFrame`, never through
 * React state: sixty renders a second of the whole panel would be the
 * stutter this tool exists to avoid. The field reads its own size each frame,
 * so a rotated phone or a resized window keeps the dot inside it.
 */
export default function BlsStimulus({ state, cyclesNow, className = "" }: Props) {
    const fieldRef = useRef<HTMLDivElement | null>(null);
    const dotRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const field = fieldRef.current;
        const dot = dotRef.current;
        if (!field || !dot) return;

        const place = () => {
            const width = field.clientWidth;
            const height = field.clientHeight;
            const diameter = Math.max(BLS_MIN_DOT_PX, width * BLS_DOT_SCALE[state.settings.dotSize]);
            const { x, y } = dotPosition(state.settings.path, cyclesNow());
            dot.style.width = `${diameter}px`;
            dot.style.height = `${diameter}px`;
            dot.style.transform = `translate(${x * Math.max(0, width - diameter)}px, ${
                y * Math.max(0, height - diameter)
            }px)`;
        };

        place();
        if (!state.running) return;
        let handle = requestAnimationFrame(function tick() {
            place();
            handle = requestAnimationFrame(tick);
        });
        return () => cancelAnimationFrame(handle);
    }, [cyclesNow, state]);

    return (
        <div
            ref={fieldRef}
            aria-hidden="true"
            className={`relative overflow-hidden ${className}`}
            style={{ backgroundColor: BLS_FIELD_COLOR }}
        >
            <div
                ref={dotRef}
                data-testid="bls-dot"
                className="absolute left-0 top-0 rounded-full will-change-transform"
                style={{ backgroundColor: BLS_DOT_HEX[state.settings.dotColor] }}
            />
        </div>
    );
}
